import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepickerToggleIcon,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import {
	BehaviorSubject,
	debounce,
	from,
	interval,
	map,
	mergeMap,
	Observable,
	of,
	Subscription,
} from 'rxjs';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/list';
import { MultiSelectTypeaheadComponent } from '../../components/multi-select-typeahead/multi-select-typeahead.component';
import { AccessDatePlaceholderPipe } from '../../components/pipes';

interface SearchConfig {
	type:
		| 'performedBy'
		| 'fromCompany'
		| 'user'
		| 'company'
		| 'device'
		| 'deviceGroup'
		| 'property'
		| 'details';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

export const emptyAuditLogFilters = (): AuditLogFilters => ({
	performedBy: [],
	fromCompany: [],
	user: [],
	company: [],
	device: [],
	deviceGroup: [],
	property: [],
	details: [],
	utcStartDate: undefined,
	utcEndDate: undefined,
});

export type AuditLogFilters = {
	performedBy?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[];
	fromCompany?: { id: string; name: string }[];
	user?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[];
	company?: { id: string; name: string }[];
	device?: { id: string; serialNumber: string }[];
	deviceGroup?: { id: string; name: string }[];
	property?: { id: string; name: string }[];
	details?: { id: string; name: string }[];
	utcStartDate?: Date;
	utcEndDate?: Date;
};

@Component({
	selector: 'app-audit-log-filter-dialog',
	templateUrl: './audit-log-filter-dialog.component.html',
	styleUrls: [
		'./audit-log-filter-dialog.component.scss',
		'../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepickerToggleIcon,
		MatDatepicker,
		MatDivider,
		MultiSelectTypeaheadComponent,
		MatDialogActions,
		MatButton,
		AccessDatePlaceholderPipe,
	],
})
export class AuditLogFilterDialogComponent implements OnDestroy, OnInit {
	auditLogFilters: AuditLogFilters = emptyAuditLogFilters();

	apiLoadingKeys = ApiLoadingKeys;

	performedByOptions?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[] = [];
	fromCompanyOptions?: { id: string; name: string }[] = [];
	userOptions?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[] = [];
	companyOptions?: { id: string; name: string }[] = [];
	deviceOptions?: { id: string; serialNumber: string }[] = [];
	deviceGroupOptions?: { id: string; name: string }[] = [];
	propertyOptions?: { id: string; name: string }[] = [];
	detailsOptions?: { id: string; name: string }[] = [];

	performedById: string[] = [];
	fromCompanyId: string[] = [];
	userId: string[] = [];
	companyId: string[] = [];
	deviceId: string[] = [];
	deviceGroupId: string[] = [];
	propertyId: string[] = [];
	detailsId: string[] = [];

	performedByMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-performedByMultiSelectSearchPlaceholder:Search Users (optional)`;
	fromCompanyMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-fromCompanyMultiSelectSearchPlaceholder:Search Companies (optional)`;
	userMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-userMultiSelectSearchPlaceholder:Search Users (optional)`;
	companyMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-companyMultiSelectSearchPlaceholder:Search Companies (optional)`;
	deviceMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-deviceMultiSelectSearchPlaceholder:Search Serial Numbers (optional)`;
	deviceGroupMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-deviceGroupMultiSelectSearchPlaceholder:Search Device Groups (optional)`;
	propertyMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-propertyMultiSelectSearchPlaceholder:Search Properties (optional)`;
	detailsMultiSelectSearchPlaceholder = $localize`:@@auditLogFilterDialog-detailsMultiSelectSearchPlaceholder:Search Details (optional)`;

	// Columns to hide based on context
	hiddenColumns: string[] = [];

	// Reset trigger for multi-select components - incrementing this will reset all multi-select components
	resetTrigger: number = 0;
	dateRangeWarning: boolean = false;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		performedBy: {
			type: 'performedBy',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) => this.apiService.getAuditReportUserOptions(params),
			mapResponse: (user) => ({
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
				displayName: `${user.firstName} ${user.lastName} (${user.email})`,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.performedBy = [...items];
			},
		},
		fromCompany: {
			type: 'fromCompany',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) => this.apiService.getAuditReportCompanyOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.fromCompany = [...items];
			},
		},
		user: {
			type: 'user',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) => this.apiService.getAuditReportUserOptions(params),
			mapResponse: (user) => ({
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
				displayName: `${user.firstName} ${user.lastName} (${user.email})`,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.user = [...items];
			},
		},
		company: {
			type: 'company',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) => this.apiService.getAuditReportCompanyOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.company = [...items];
			},
		},
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) => this.apiService.getAuditReportDeviceOptions(params),
			mapResponse: (device) => ({
				id: device.id,
				serialNumber: device.serialNumber,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.device = [...items];
			},
		},
		deviceGroup: {
			type: 'deviceGroup',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) =>
				this.apiService.getAuditReportDeviceGroupOptions(params),
			mapResponse: (deviceGroup) => ({
				id: deviceGroup.id,
				name: deviceGroup.name,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.deviceGroup = [...items];
			},
		},
		property: {
			type: 'property',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) =>
				this.apiService.getAuditReportPropertyOptions(params),
			mapResponse: (property) => ({
				id: property.id,
				name: property.name,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.property = [...items];
			},
		},
		details: {
			type: 'details',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: [],
			apiCall: (params) => this.apiService.getAuditReportDetailsOptions(params),
			mapResponse: (detail) => ({
				id: Array.isArray(detail.ids) ? detail.ids.join(',') : detail.id,
				name: detail.description,
			}),
			updateFilters: (items) => {
				this.auditLogFilters.details = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<AuditLogFilterDialogComponent>,
		private readonly apiService: ApiService,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters?: AuditLogFilters;
			hiddenColumns?: string[];
			deviceId?: string;
			userId?: string;
			companyId?: string;
			propertyId?: string;
			deviceGroupId?: string;
			roleId?: string;
		}
	) {
		this.auditLogFilters = data?.reportFilters ?? this.getDefaultFilters();
		this.hiddenColumns = data?.hiddenColumns ?? [];

		if (data?.reportFilters) {
			this.auditLogFilters.performedBy?.forEach((user) => {
				this.performedById.push(user.id);
			});
			this.auditLogFilters.fromCompany?.forEach((company) => {
				this.fromCompanyId.push(company.id);
			});
			this.auditLogFilters.user?.forEach((user) => {
				this.userId.push(user.id);
			});
			this.auditLogFilters.company?.forEach((company) => {
				this.companyId.push(company.id);
			});
			this.auditLogFilters.device?.forEach((device) => {
				this.deviceId.push(device.id);
			});
			this.auditLogFilters.deviceGroup?.forEach((deviceGroup) => {
				this.deviceGroupId.push(deviceGroup.id);
			});
			this.auditLogFilters.property?.forEach((property) => {
				this.propertyId.push(property.id);
			});
			this.auditLogFilters.details?.forEach((detail) => {
				this.detailsId.push(detail.id);
			});
		}

		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	ngOnInit(): void {
		// Initialize default date filters if not already set
		const defaultFilters = this.getDefaultFilters();
		if (!this.auditLogFilters.utcStartDate) {
			this.auditLogFilters.utcStartDate = defaultFilters.utcStartDate;
		}
		if (!this.auditLogFilters.utcEndDate) {
			this.auditLogFilters.utcEndDate = defaultFilters.utcEndDate;
		}
	}

	/**
	 * Get default filters: 1 month start date, no end date
	 */
	getDefaultFilters(): AuditLogFilters {
		const oneMonthAgo = new Date();
		oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

		return {
			...emptyAuditLogFilters(),
			utcStartDate: oneMonthAgo,
			utcEndDate: undefined,
		};
	}

	get primaryButtonText(): string {
		return $localize`:@@auditLogFilterDialog-generate:Generate`;
	}

	/**
	 * Check if field should be hidden based on context
	 */
	isFieldHidden(columnName: string): boolean {
		return this.hiddenColumns.includes(columnName);
	}

	onDateStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		const newStartDate = event.value;
		if (!newStartDate) {
			this.auditLogFilters.utcStartDate = undefined;
			return;
		}

		this.auditLogFilters.utcStartDate = newStartDate;

		// If start date is set and there's an end date, validate the 6-month constraint
		if (this.auditLogFilters.utcEndDate) {
			const sixMonthsLater = new Date(newStartDate);
			sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);

			// If end date is more than 6 months after start date, auto-correct it
			if (this.auditLogFilters.utcEndDate > sixMonthsLater) {
				this.auditLogFilters.utcEndDate = sixMonthsLater;
				this.dateRangeWarning = true;
			} else {
				this.dateRangeWarning = false;
			}
		}
	}

	onDateEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		const newEndDate = event.value;
		if (!newEndDate) {
			this.auditLogFilters.utcEndDate = undefined;
			return;
		}

		this.auditLogFilters.utcEndDate = newEndDate;

		// If end date is set and there's a start date, validate the 6-month constraint
		if (this.auditLogFilters.utcStartDate) {
			const sixMonthsEarlier = new Date(newEndDate);
			sixMonthsEarlier.setMonth(sixMonthsEarlier.getMonth() - 6);

			// If start date is more than 6 months before end date, auto-correct it
			if (this.auditLogFilters.utcStartDate < sixMonthsEarlier) {
				this.auditLogFilters.utcStartDate = sixMonthsEarlier;
				this.dateRangeWarning = true;
			} else {
				this.dateRangeWarning = false;
			}
		}
	}

	/**
	 * Get max date for start date picker (cannot be after end date)
	 */
	getMaxStartDate(): Date | undefined {
		return this.auditLogFilters.utcEndDate;
	}

	/**
	 * Get min date for end date picker (cannot be before start date)
	 */
	getMinEndDate(): Date | undefined {
		return this.auditLogFilters.utcStartDate;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	onClearAll(): void {
		// Reset filters to default state (with default dates)
		const defaultFilters = this.getDefaultFilters();
		this.auditLogFilters = {
			...defaultFilters,
			performedBy: [],
			fromCompany: [],
			user: [],
			company: [],
			device: [],
			deviceGroup: [],
			property: [],
			details: [],
		};

		this.performedById = [];
		this.fromCompanyId = [];
		this.userId = [];
		this.companyId = [];
		this.deviceId = [];
		this.deviceGroupId = [];
		this.propertyId = [];
		this.detailsId = [];

		this.performedByOptions = [];
		this.fromCompanyOptions = [];
		this.userOptions = [];
		this.companyOptions = [];
		this.deviceOptions = [];
		this.deviceGroupOptions = [];
		this.propertyOptions = [];
		this.detailsOptions = [];

		Object.keys(this.searchConfigs).forEach((key) => {
			const config = this.searchConfigs[key];
			config.selectedItems.clear();
			config.updateFilters([]);
			config.searchEvents.next('');
		});

		// Increment reset trigger to reset all multi-select components
		this.resetTrigger++;
	}

	onGenerateReport(): void {
		const compiledFilters = this.compileFilters();

		this.dialogRef.close({
			filters: this.auditLogFilters,
			compiledFilters,
		});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		// Add date filters - these are required
		if (this.auditLogFilters.utcStartDate) {
			// Set to beginning of day (00:00:00)
			const startDate = new Date(this.auditLogFilters.utcStartDate);
			startDate.setHours(0, 0, 0, 0);
			queryParameters.push({
				param: 'secondaryFilterUtcStartDate',
				value: startDate.toISOString(),
			});
		}

		if (this.auditLogFilters.utcEndDate) {
			// Set to end of day (23:59:59.999)
			const endDate = new Date(this.auditLogFilters.utcEndDate);
			endDate.setHours(23, 59, 59, 999);
			queryParameters.push({
				param: 'secondaryFilterUtcEndDate',
				value: endDate.toISOString(),
			});
		}

		// Add entity filters
		if (
			this.auditLogFilters.performedBy &&
			this.auditLogFilters.performedBy.length > 0
		) {
			queryParameters.push({
				param: 'secondaryFilterPerformedByUserIds',
				value: this.auditLogFilters.performedBy.map((it) => it.id),
			});
		}

		if (
			this.auditLogFilters.fromCompany &&
			this.auditLogFilters.fromCompany.length > 0
		) {
			queryParameters.push({
				param: 'secondaryFilterFromCompanyIds',
				value: this.auditLogFilters.fromCompany.map((it) => it.id),
			});
		}

		if (this.auditLogFilters.user && this.auditLogFilters.user.length > 0) {
			queryParameters.push({
				param: 'secondaryFilterUserIds',
				value: this.auditLogFilters.user.map((it) => it.id),
			});
		}

		if (
			this.auditLogFilters.company &&
			this.auditLogFilters.company.length > 0
		) {
			queryParameters.push({
				param: 'secondaryFilterCompanyIds',
				value: this.auditLogFilters.company.map((it) => it.id),
			});
		}

		if (this.auditLogFilters.device && this.auditLogFilters.device.length > 0) {
			queryParameters.push({
				param: 'secondaryFilterDeviceIds',
				value: this.auditLogFilters.device.map((it) => it.id),
			});
		}

		if (
			this.auditLogFilters.deviceGroup &&
			this.auditLogFilters.deviceGroup.length > 0
		) {
			queryParameters.push({
				param: 'secondaryFilterDeviceGroupIds',
				value: this.auditLogFilters.deviceGroup.map((it) => it.id),
			});
		}

		if (
			this.auditLogFilters.property &&
			this.auditLogFilters.property.length > 0
		) {
			queryParameters.push({
				param: 'secondaryFilterPropertyIds',
				value: this.auditLogFilters.property.map((it) => it.id),
			});
		}

		if (
			this.auditLogFilters.details &&
			this.auditLogFilters.details.length > 0
		) {
			// Details can have comma-separated IDs (e.g., "1,2,3"), so we need to split and flatten them
			const detailIds = this.auditLogFilters.details.flatMap((it) =>
				it.id.toString().includes(',') ? it.id.toString().split(',') : [it.id]
			);
			queryParameters.push({
				param: 'secondaryFilterDetails',
				value: detailIds,
			});
		}

		return queryParameters;
	}

	initializeData(): void {
		if (this.data?.reportFilters) {
			this.auditLogFilters = this.data.reportFilters;

			// Initialize performedByOptions with previous selections
			if (this.auditLogFilters.performedBy?.length) {
				this.auditLogFilters.performedBy.forEach((user) => {
					const userOption = {
						id: user.id,
						firstName: user.firstName,
						lastName: user.lastName,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName} (${user.email})`,
					};
					this.performedById.push(user.id);
					this.searchConfigs.performedBy.selectedItems.add(userOption);
				});
				this.performedByOptions = Array.from(
					this.searchConfigs.performedBy.selectedItems
				);
			}

			// Initialize fromCompanyOptions with previous selections
			if (this.auditLogFilters.fromCompany?.length) {
				this.auditLogFilters.fromCompany.forEach((company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.fromCompanyId.push(company.id);
					this.searchConfigs.fromCompany.selectedItems.add(companyOption);
				});
				this.fromCompanyOptions = Array.from(
					this.searchConfigs.fromCompany.selectedItems
				);
			}

			// Initialize userOptions with previous selections
			if (this.auditLogFilters.user?.length) {
				this.auditLogFilters.user.forEach((user) => {
					const userOption = {
						id: user.id,
						firstName: user.firstName,
						lastName: user.lastName,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName} (${user.email})`,
					};
					this.userId.push(user.id);
					this.searchConfigs.user.selectedItems.add(userOption);
				});
				this.userOptions = Array.from(this.searchConfigs.user.selectedItems);
			}

			// Initialize companyOptions with previous selections
			if (this.auditLogFilters.company?.length) {
				this.auditLogFilters.company.forEach((company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.companyId.push(company.id);
					this.searchConfigs.company.selectedItems.add(companyOption);
				});
				this.companyOptions = Array.from(
					this.searchConfigs.company.selectedItems
				);
			}

			// Initialize deviceOptions with previous selections
			if (this.auditLogFilters.device?.length) {
				this.auditLogFilters.device.forEach((device) => {
					const deviceOption = {
						id: device.id,
						serialNumber: device.serialNumber,
					};
					this.deviceId.push(device.id);
					this.searchConfigs.device.selectedItems.add(deviceOption);
				});
				this.deviceOptions = Array.from(
					this.searchConfigs.device.selectedItems
				);
			}

			// Initialize deviceGroupOptions with previous selections
			if (this.auditLogFilters.deviceGroup?.length) {
				this.auditLogFilters.deviceGroup.forEach((deviceGroup) => {
					const deviceGroupOption = {
						id: deviceGroup.id,
						name: deviceGroup.name,
					};
					this.deviceGroupId.push(deviceGroup.id);
					this.searchConfigs.deviceGroup.selectedItems.add(deviceGroupOption);
				});
				this.deviceGroupOptions = Array.from(
					this.searchConfigs.deviceGroup.selectedItems
				);
			}

			// Initialize propertyOptions with previous selections
			if (this.auditLogFilters.property?.length) {
				this.auditLogFilters.property.forEach((property) => {
					const propertyOption = {
						id: property.id,
						name: property.name,
					};
					this.propertyId.push(property.id);
					this.searchConfigs.property.selectedItems.add(propertyOption);
				});
				this.propertyOptions = Array.from(
					this.searchConfigs.property.selectedItems
				);
			}

			// Initialize detailsOptions with previous selections
			if (this.auditLogFilters.details?.length) {
				this.auditLogFilters.details.forEach((detail) => {
					const detailOption = {
						id: detail.id,
						name: detail.name,
					};
					this.detailsId.push(detail.id);
					this.searchConfigs.details.selectedItems.add(detailOption);
				});
				this.detailsOptions = Array.from(
					this.searchConfigs.details.selectedItems
				);
			}
		}
	}

	setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	handleSearch(searchTerm: string, config: SearchConfig): Observable<any[]> {
		if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
			switch (config.type) {
				case 'performedBy':
					return of(this.performedByOptions || []);
				case 'fromCompany':
					return of(this.fromCompanyOptions || []);
				case 'user':
					return of(this.userOptions || []);
				case 'company':
					return of(this.companyOptions || []);
				case 'device':
					return of(this.deviceOptions || []);
				case 'deviceGroup':
					return of(this.deviceGroupOptions || []);
				case 'property':
					return of(this.propertyOptions || []);
				case 'details':
					return of(this.detailsOptions || []);
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				// Update the corresponding options array based on the config type
				switch (config.type) {
					case 'performedBy':
						this.performedByOptions = mergedOptions;
						break;
					case 'fromCompany':
						this.fromCompanyOptions = mergedOptions;
						break;
					case 'user':
						this.userOptions = mergedOptions;
						break;
					case 'company':
						this.companyOptions = mergedOptions;
						break;
					case 'device':
						this.deviceOptions = mergedOptions;
						break;
					case 'deviceGroup':
						this.deviceGroupOptions = mergedOptions;
						break;
					case 'property':
						this.propertyOptions = mergedOptions;
						break;
					case 'details':
						this.detailsOptions = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}

	getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'performedBy':
				return 'name';
			case 'fromCompany':
				return 'companyName';
			case 'user':
				return 'name';
			case 'company':
				return 'companyName';
			case 'device':
				return 'serialNumber';
			case 'deviceGroup':
				return 'name';
			case 'property':
				return 'name';
			case 'details':
				return 'search';
			default:
				return 'name';
		}
	}

	mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
			// Clear selected items
			config.selectedItems.clear();
			// Complete the BehaviorSubject to prevent memory leaks
			config.searchEvents.complete();
		});
	}
}
